/**
 * B632 (c) — the `/me` controller: opens the email's link (once), keeps the
 * signed-in session under its own per-tenant key, asks for a link, and reads
 * the email's orders under the session (order.list's B25c fence). TICKETS
 * reaches the QR codes through order.tokens (the code + the signed-in email)
 * and the B606 ticket page.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { EmptyState, GENERIC_VOCABULARY, isTenantType, vocabularyFor } from "@transact/ui";
import { ApiError, createApiClient } from "../lib/api.js";
import { resolveSiteKey } from "../lib/boot.js";
import {
  createMeStore,
  emailWhy,
  forgetMeAsk,
  magicLinkScope,
  meCards,
  meOrderFace,
  meMembershipUrl,
  meOrdersUrl,
  meOrderUrl,
  meTickets,
  meTicketsUrl,
  meToday,
  meVenueFacts,
  recallMeAsk,
  rememberMeAsk,
  type MeOrder,
  type MeOrderDetail,
  type MeRoute,
  type MeSession,
  type MeTicket,
  type MeVenueFacts,
} from "../lib/me.js";
import { meMoveChoices, meMovedWords, meMoveSplitWords, meMoveTarget, type MeFitSlot, type MeMoveDay, type MeMoveTarget } from "../lib/meMove.js";
import { ticketQrDataUrl } from "../lib/qr.js";
import { recallBusinessName, recallClock, recallTenantType, rememberBusinessName, rememberClock, rememberTenantType } from "../lib/theme.js";
import { paintRememberedTheme, paintVenueTheme } from "../lib/paintVenueTheme.js";
import { MeChangeSheet, MeOrderPage, MeOrders, MeSignIn, type MeResendState, type MeSignInPhase } from "./mePage.js";
import { MeAddSheet } from "./meAddSheet.js";
import { MeMembershipPage } from "./meMembershipPage.js";
import { useMeMembership } from "./useMeMembership.js";
import { useMemberRenew } from "./useMemberRenew.js";
import { useGiftRedeem } from "./useGiftRedeem.js";
import { memberGreeting, membershipFaces } from "../lib/meMembership.js";
import { useMeAdd } from "./useMeAdd.js";
import { createSetupGateway, loadStripeFactory, type StripeFactory } from "../lib/gateway.js";
import { documentPaymentAppearance } from "../lib/paymentAppearance.js";
import { MembershipCardStep } from "./membershipCheckout.js";
import { cardStepNote } from "../lib/membershipCheckout.js";

interface Caller {
  call(op: string, fields: Record<string, unknown>): Promise<unknown>;
}

/** How long Resend waits after a send — the door's own rate wall is 5 an hour; this keeps a double press from spending two. */
const RESEND_AFTER_S = 60;

export function MeApp(props: {
  readonly route: MeRoute;
  readonly apiBaseUrl: string;
  readonly storage?: Storage | null;
  readonly now?: () => number;
  /** The buyer's own date, "YYYY-MM-DD" (a seam for tests). */
  readonly today?: string;
  /** Drops the spent link from the address bar (history.replaceState). */
  readonly clearHash?: (path: string) => void;
  readonly go?: (url: string) => void;
  /** B703 (a) — the page's own origin, for the card page's way back (default: this tab's). */
  readonly origin?: string;
  /** A test seam: the API client for a tenant, with or without the session's token. */
  readonly client?: (tenantId: string, token?: string) => Caller;
  /**
   * B722 — the venue's facts (name, logo, slot noun, clock) from the guest door, read on every load: a buyer who
   * opens the link in a mail app's browser has no storefront visit to recall. A test seam; the page's own read by default
   * (a test that passes only `client` reads none, so its exact call lists stay the page's own calls).
   */
  readonly facts?: (tenantId: string, channelId: string) => Promise<MeVenueFacts | undefined>;
  /** A test seam: the QR image for a ticket token (the package's one mint by default). */
  readonly qr?: (token: string) => Promise<string>;
  /** PRINT TICKETS (window.print by default). */
  readonly print?: () => void;
  /** B632 (e2) seams: the provider's script, the confirm poll's cadence, the buyer's cart id. */
  readonly stripe?: () => Promise<StripeFactory | undefined>;
  readonly pollMs?: number;
  readonly newCartId?: () => string;
}): ReactElement {
  const now = props.now ?? Date.now;
  const storage = props.storage === undefined ? safeSession() : props.storage;
  const client = useCallback(
    (tenantId: string, token?: string): Caller =>
      props.client?.(tenantId, token) ??
      (createApiClient({ baseUrl: props.apiBaseUrl, tenantId, ...(token !== undefined ? { token: () => token } : {}) }) as unknown as Caller),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the seam is fixed for the page's life
    [props.apiBaseUrl],
  );
  const linkScope = props.route.linkToken !== undefined ? magicLinkScope(props.route.linkToken) : undefined;
  /* whose page this is: the link names it; else the storefront's own query (a site key resolves to tenant + channel) */
  const [scope, setScope] = useState<{ readonly tenantId: string; readonly channelId: string } | undefined | "unknown">(() =>
    linkScope ?? (props.route.tenantId !== undefined ? { tenantId: props.route.tenantId, channelId: "web" } : props.route.siteKey !== undefined ? undefined : "unknown"),
  );
  useEffect(() => {
    if (scope !== undefined || props.route.siteKey === undefined) return;
    void resolveSiteKey(props.apiBaseUrl, props.route.siteKey).then((ch) => setScope(ch ?? "unknown"));
  }, [scope, props.route.siteKey, props.apiBaseUrl]);
  const tenantId = typeof scope === "object" ? scope.tenantId : undefined;
  const store = useMemo(() => (tenantId === undefined ? undefined : createMeStore(storage, tenantId, now)), [storage, tenantId, now]);

  const [session, setSession] = useState<MeSession | undefined>(undefined);
  const [phase, setPhase] = useState<MeSignInPhase | "opening">(props.route.linkToken !== undefined && linkScope !== undefined ? "opening" : { kind: "ask", refused: props.route.linkToken !== undefined });
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState<readonly MeOrder[] | undefined>(undefined);
  const [ordersError, setOrdersError] = useState<string | undefined>(undefined);
  const [ticketsBusy, setTicketsBusy] = useState<string | undefined>(undefined);
  const [ticketsError, setTicketsError] = useState<{ readonly orderId: string; readonly message: string } | undefined>(undefined);

  /* the link from the email: spend it once, keep the session, drop the link from the address bar */
  useEffect(() => {
    if (props.route.linkToken === undefined || linkScope === undefined || store === undefined) return;
    let gone = false;
    void client(linkScope.tenantId)
      .call("session.magiclink.open", { token: props.route.linkToken })
      .then((r) => {
        if (gone) return;
        const s = r as MeSession;
        store.set({ token: s.token, customerId: s.customerId, email: s.email, expiresAt: s.expiresAt });
        setSession(store.get() ?? s);
        /* B722 — the buyer asked for one order before signing in (maybe in another tab): go back to it, once */
        const asked = recallMeAsk(storage, linkScope.tenantId);
        if (asked !== undefined) {
          forgetMeAsk(storage, linkScope.tenantId);
          (props.go ?? ((u: string) => window.location.assign(u)))(meOrderUrl(linkScope.tenantId, asked));
        }
      })
      .catch(() => {
        if (!gone) setPhase({ kind: "ask", refused: true });
      })
      .finally(() => {
        /* B709 leg 2 — a link opened on the member portal stays on it */
        props.clearHash?.(props.route.membership === true ? meMembershipUrl(linkScope.tenantId) : `/me?tenant=${encodeURIComponent(linkScope.tenantId)}`);
      });
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per link
  }, [store]);

  /* no link: a session kept from an earlier visit signs the page in */
  useEffect(() => {
    if (props.route.linkToken !== undefined || store === undefined || tenantId === undefined) return;
    const kept = store.get();
    if (kept !== undefined) setSession(kept);
    /* B722 — signed out on one order's page: keep the ask, so the link comes back to it */
    else if (props.route.orderId !== undefined) rememberMeAsk(storage, tenantId, props.route.orderId);
  }, [store, props.route.linkToken]);

  /* B722 — the venue's facts from the guest door, every load; the recall below is only the first paint */
  const [venue, setVenue] = useState<MeVenueFacts | undefined>(undefined);
  useEffect(() => {
    if (typeof document !== "undefined") paintRememberedTheme(); // B819 — the last visit's colors before the door answers
  }, []);
  const channelId = typeof scope === "object" ? scope.channelId : undefined;
  useEffect(() => {
    if (tenantId === undefined || channelId === undefined) return;
    const read =
      props.facts ??
      (props.client !== undefined
        ? undefined
        : (t: string, c: string) => client(t).call("session.guest", { channelId: c }).then(meVenueFacts));
    if (read === undefined) return;
    let gone = false;
    void read(tenantId, channelId)
      .then((f) => {
        if (gone || f === undefined) return;
        setVenue(f);
        paintVenueTheme(f.theme); // B819 — the venue's colors, as the hosted page wears them
        try {
          if (f.businessName !== undefined) rememberBusinessName(window.sessionStorage, f.businessName);
          if (f.tenantType !== undefined) rememberTenantType(window.sessionStorage, f.tenantType);
          if (f.timezone !== undefined || f.timeFormat !== undefined)
            rememberClock(window.sessionStorage, { ...(f.timezone !== undefined ? { timezone: f.timezone } : {}), ...(f.timeFormat !== undefined ? { timeFormat: f.timeFormat } : {}) });
        } catch {
          /* memory-only visits read again next time */
        }
      })
      .catch(() => undefined);
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per tenant
  }, [tenantId, channelId]);

  const readOrders = useCallback(() => {
    /* an order's own page reads that order, not the list; the member portal reads memberships */
    if (session === undefined || tenantId === undefined || props.route.orderId !== undefined || props.route.membership === true) return () => undefined;
    let gone = false;
    setOrdersError(undefined);
    setOrders(undefined);
    void client(tenantId, session.token)
      .call("order.list", { customerId: session.customerId })
      .then((r) => {
        if (!gone) setOrders((r as { orders: readonly MeOrder[] }).orders);
      })
      .catch((e: unknown) => {
        if (gone) return;
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          /* the session ended on the server's clock: sign in again */
          store?.clear();
          setSession(undefined);
          setPhase({ kind: "ask" });
          return;
        }
        setOrdersError("We couldn't read your orders just now. Nothing changed — your tickets are also in your confirmation email.");
      });
    return () => {
      gone = true;
    };
  }, [client, session, tenantId, store, props.route.orderId]);
  useEffect(() => readOrders(), [readOrders]);

  /* B632 (c) region 2 — one order's page: order.get + entitlement.list under the session, the tokens by code + the signed-in email */
  const orderId = props.route.orderId;
  const [detail, setDetail] = useState<{ readonly order: MeOrderDetail; readonly tickets: readonly MeTicket[] } | undefined>(undefined);
  const [detailError, setDetailError] = useState<string | undefined>(undefined);
  const [qrs, setQrs] = useState<Readonly<Record<string, string>>>({});
  const [resend, setResend] = useState<MeResendState | undefined>(undefined);
  const readOrder = useCallback(() => {
    if (orderId === undefined || session === undefined || tenantId === undefined) return () => undefined;
    let gone = false;
    setDetail(undefined);
    setDetailError(undefined);
    const signedIn = client(tenantId, session.token);
    void (async () => {
      const got = (await signedIn.call("order.get", { orderId })) as { order?: MeOrderDetail };
      if (got.order === undefined) {
        if (!gone) setDetailError(`This order isn't under ${session.email}. Your orders lists the ones that are.`);
        return;
      }
      const order = got.order;
      const listed = (await signedIn.call("entitlement.list", { orderId })) as { entitlements: readonly { entitlementId: string; state: string; variantName?: string }[] };
      /* the QR codes: the same tokens the ticket email carries, through the code + the signed-in email; a miss keeps the rows */
      const tokens =
        order.confirmationCode === undefined
          ? []
          : await client(tenantId)
              .call("order.tokens", { code: order.confirmationCode, email: session.email })
              .then((r) => (r as { order?: { tokens: readonly { entitlementId: string; token: string }[] } }).order?.tokens ?? [])
              .catch(() => []);
      if (gone) return;
      const tickets = meTickets(listed.entitlements, tokens);
      setDetail({ order, tickets });
      const mint = props.qr ?? ticketQrDataUrl;
      for (const t of tickets) {
        if (t.token === undefined || !(t.state === "ISSUED" || t.state === "ACTIVE")) continue;
        const token = t.token;
        void mint(token).then((src) => {
          if (!gone) setQrs((q) => ({ ...q, [token]: src }));
        });
      }
    })().catch((e: unknown) => {
      if (gone) return;
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        store?.clear();
        setSession(undefined);
        setPhase({ kind: "ask" });
        return;
      }
      setDetailError("We couldn't read this order just now. Nothing changed — your tickets are also in your confirmation email.");
    });
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the QR seam is fixed for the page's life
  }, [client, session, tenantId, orderId, store]);
  useEffect(() => readOrder(), [readOrder]);

  const resendTickets = (): void => {
    if (orderId === undefined || session === undefined || tenantId === undefined || resend?.kind === "busy") return;
    setResend({ kind: "busy" });
    void client(tenantId, session.token)
      .call("order.resend", { orderId })
      .then(() => setResend({ kind: "sent" }))
      .catch(() => setResend({ kind: "failed" }));
  };

  /* B632 (e) — the Change sheet: slot.fit for the party on the order's night, then order.move per line, a re-send, a re-read.
     B838 — the sheet holds its own party (`target`, frozen at open) and where each line it moved now sits (`at`):
     a refusal after part of the party moved is said as a split, the order behind is re-read, and MOVE again moves
     only the lines not already on the pick (re-sending a moved line is refused "already for that showtime"). */
  const [change, setChange] = useState<
    | {
        readonly target: MeMoveTarget;
        readonly nowWords: string | undefined;
        readonly currency: string;
        readonly at: Readonly<Record<string, string>>;
        readonly days?: readonly MeMoveDay[] | undefined;
        readonly readError?: string | undefined;
        readonly picked?: string;
        readonly busy?: boolean;
        readonly error?: string | undefined;
      }
    | undefined
  >(undefined);
  const [moved, setMoved] = useState<string | undefined>(undefined);
  const changeTarget = detail === undefined ? undefined : meMoveTarget(detail.order);
  const readShowtimes = (target: MeMoveTarget): void => {
    if (session === undefined || tenantId === undefined || typeof scope !== "object") return;
    setChange((c) => (c === undefined ? c : { ...c, days: undefined, readError: undefined }));
    void client(tenantId, session.token)
      .call("slot.fit", { scheduleId: target.scheduleId, productId: target.productId, channelId: scope.channelId, party: target.party })
      .then((r) => setChange((c) => (c === undefined ? c : { ...c, days: meMoveChoices((r as { slots: readonly MeFitSlot[] }).slots, target.fromSlotId, now(), unit ?? GENERIC_VOCABULARY.capacityUnit.singular) })))
      .catch(() => setChange((c) => (c === undefined ? c : { ...c, readError: "We couldn't read the other times just now. Nothing changed." })));
  };
  const openChange = (): void => {
    if (changeTarget === undefined || detail === undefined || session === undefined || tenantId === undefined || typeof scope !== "object") return;
    setChange({
      target: changeTarget,
      nowWords: meOrderFace(detail.order, detail.tickets, { timeZone: venueZone }).when,
      currency: detail.order.total.currency,
      at: {},
    });
    readShowtimes(changeTarget);
  };
  const moveTickets = (): void => {
    const to = change?.picked;
    if (change === undefined || to === undefined || change.busy === true || orderId === undefined || session === undefined || tenantId === undefined) return;
    const { target } = change;
    const signedIn = client(tenantId, session.token);
    const at: Record<string, string> = { ...change.at };
    const pending = target.lineIds.filter((l) => (at[l] ?? target.fromSlotId) !== to);
    setChange({ ...change, busy: true, error: undefined });
    void (async () => {
      /* the op moves a line; the party on the night moves together, one line at a time */
      for (const lineId of pending) {
        await signedIn.call("order.move", { orderId, lineId, slotId: to });
        at[lineId] = to;
      }
    })()
      .then(async () => {
        /* the new tickets reach the inbox; a failed send leaves RESEND EMAIL on the page */
        await signedIn.call("order.resend", { orderId }).catch(() => undefined);
        setChange(undefined);
        setMoved(meMovedWords(to));
        readOrder();
      })
      .catch((e: unknown) => {
        const refused = typeof e === "object" && e !== null && (e as { code?: unknown }).code === "REFUSED" && e instanceof Error;
        const split = meMoveSplitWords(target, at);
        /* a line landed before the refusal: the page behind must stop showing the old night */
        if (pending.some((l) => at[l] === to)) readOrder();
        const why = refused
          ? e.message
          : split !== undefined
            ? "We couldn't move the rest just now — try again in a moment."
            : "We couldn't move your tickets just now. Nothing changed — try again in a moment.";
        /* a sheet closed while the move flew stays closed (B838) */
        setChange((c) => (c === undefined ? c : { ...c, at, busy: false, error: split !== undefined ? `${split} ${why}` : why }));
      });
  };

  /* B632 (e2) — the Add sheet: the buyer's cart, order.lines.add, their own checkout */
  const add = useMeAdd({
    signedIn: session !== undefined && tenantId !== undefined ? client(tenantId, session.token) : undefined,
    tenantId,
    channelId: typeof scope === "object" ? scope.channelId : undefined,
    order: detail?.order,
    go: props.go ?? ((u: string) => window.location.assign(u)),
    stripe: props.stripe,
    pollMs: props.pollMs,
    newCartId: props.newCartId,
  });

  /* B709 leg 2 — the member portal: the buyer's own memberships and their plans, under the session */
  /* B815 — read on the orders face too (Pathik, 2026-10-13: "/me does not
     show membership"): a guest who holds a membership gets its door beside
     SIGN OUT, and the orders page says so above the orders. */
  const memberCaller = useMemo(
    () => (session !== undefined && tenantId !== undefined && props.route.orderId === undefined ? client(tenantId, session.token) : undefined),
    [client, session, tenantId, props.route.orderId],
  );
  const portal = useMeMembership(memberCaller, () => {
    store?.clear();
    setSession(undefined);
    setPhase({ kind: "ask" });
  });

  /* Resend's clock */
  const resendIn = typeof phase === "object" && phase.kind === "sent" ? phase.resendIn : 0;
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setPhase((p) => (typeof p === "object" && p.kind === "sent" ? { ...p, resendIn: p.resendIn - 1 } : p)), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const send = (): void => {
    if (typeof scope !== "object") return;
    const why = emailWhy(email);
    if (why !== undefined) {
      setPhase({ kind: "ask", error: why });
      return;
    }
    const to = email.trim();
    setPhase((p) => (typeof p === "object" && p.kind === "sent" ? { ...p, busy: true } : { kind: "ask", busy: true }));
    void client(scope.tenantId)
      .call("session.magiclink.request", { email: to, channelId: scope.channelId })
      .then(() => setPhase({ kind: "sent", to, resendIn: RESEND_AFTER_S }))
      .catch(() => setPhase({ kind: "ask", failed: true }));
  };

  const tickets = (orderId: string): void => {
    const order = orders?.find((o) => o.orderId === orderId);
    if (order?.confirmationCode === undefined || session === undefined || tenantId === undefined) return;
    setTicketsBusy(orderId);
    setTicketsError(undefined);
    void client(tenantId)
      .call("order.tokens", { code: order.confirmationCode, email: session.email })
      .then((r) => {
        const tokens = (r as { order?: { tokens: readonly { token: string }[] } }).order?.tokens.map((t) => t.token) ?? [];
        if (tokens.length === 0) {
          setTicketsError({ orderId, message: "These tickets can't be shown here yet — they're also in your confirmation email." });
          return;
        }
        (props.go ?? ((u: string) => window.location.assign(u)))(meTicketsUrl(tokens));
      })
      .catch(() => setTicketsError({ orderId, message: "We couldn't open your tickets just now. Try again — they're also in your confirmation email." }))
      .finally(() => setTicketsBusy(undefined));
  };

  /* the storefront remembers its tenant's name for the visit (B403 #26) — the first paint; B722: the guest door's answer wins */
  const recalledName = useMemo(() => {
    try {
      return recallBusinessName(window.sessionStorage);
    } catch {
      return undefined;
    }
  }, []);
  const businessName = venue?.businessName ?? recalledName;
  const logoUrl = venue?.logoUrl;

  /* B659 — the venue's zone, recalled from the storefront's own visit (B621's clock mint); unknown = dates that need it drop.
     B126 — the tenant's slot noun the same way (a SHOW's showtime, a FERRY's departure); unknown = the generic pack. */
  const { venueZone, unit } = useMemo(() => {
    try {
      const t = venue?.tenantType ?? recallTenantType(window.sessionStorage);
      return {
        venueZone: venue?.timezone ?? recallClock(window.sessionStorage)?.timezone,
        unit: vocabularyFor(t !== undefined && isTenantType(t) ? t : undefined).capacityUnit.singular,
      };
    } catch {
      return { venueZone: undefined, unit: undefined };
    }
  }, [venue]);
  /* B703 (a) — RENEW EARLY on the buyer's own door */
  const renewal = useMemberRenew({
    caller: memberCaller,
    tenantId,
    members: portal.members,
    plans: portal.plans,
    timeZone: venueZone,
    origin: props.origin ?? (typeof window !== "undefined" ? window.location.origin : ""),
    returning: props.route.renew,
    inPage: venue?.publishableKey !== undefined, // B823
    go: props.go ?? ((u: string) => window.location.assign(u)),
    clearAddress: props.clearHash,
    reread: portal.retry,
    onSignedOut: () => {
      store?.clear();
      setSession(undefined);
      setPhase({ kind: "ask" });
    },
  });
  /* B709 (a3) — REDEEM MY GIFT on the recipient's own session */
  const gifting = useGiftRedeem({
    caller: memberCaller,
    timeZone: venueZone,
    reread: portal.retry,
    onSignedOut: () => {
      store?.clear();
      setSession(undefined);
      setPhase({ kind: "ask" });
    },
  });
  /* B722 — a signed-out buyer who asked for their orders hears why the page asks */
  const asked = props.route.asked === true || props.route.orderId !== undefined || props.route.membership === true;
  const askedFor = props.route.membership === true ? ("membership" as const) : undefined;

  if (scope === "unknown")
    return (
      <main className="me" aria-label="Your tickets">
        <div className="me-body">
          <EmptyState
            title="Open this from your confirmation email"
            body="This page needs to know where you bought your tickets. The link in your confirmation email opens them."
          />
        </div>
      </main>
    );
  if (session !== undefined && orderId !== undefined && tenantId !== undefined)
    return (
      <>
      <MeOrderPage
        face={detail === undefined ? undefined : meOrderFace(detail.order, detail.tickets, { timeZone: venueZone, today: props.today ?? meToday(now()), ...(unit !== undefined ? { unit } : {}) })}
        error={detailError}
        qr={qrs}
        backHref={meOrdersUrl(tenantId)}
        businessName={businessName}
        logoUrl={logoUrl}
        resend={resend}
        onPrint={props.print ?? (() => window.print())}
        onResend={resendTickets}
        onRetry={() => void readOrder()}
        moved={moved}
        {...(changeTarget !== undefined ? { onChange: openChange } : {})}
        {...(add.offered ? { onAdd: add.open } : {})}
        onSignOut={() => {
          store?.clear();
          setSession(undefined);
          setDetail(undefined);
          setPhase({ kind: "ask" });
        }}
      />
      {change !== undefined ? (
        <MeChangeSheet
          open
          unit={unit ?? GENERIC_VOCABULARY.capacityUnit.singular}
          target={change.target}
          nowWords={change.nowWords}
          days={change.days}
          readError={change.readError}
          onReread={() => readShowtimes(change.target)}
          picked={change.picked}
          currency={change.currency}
          busy={change.busy === true}
          error={change.error}
          onPick={(slotId) => setChange((c) => (c === undefined || c.busy === true ? c : { ...c, picked: slotId, error: undefined }))}
          onMove={moveTickets}
          onClose={() => setChange(undefined)}
        />
      ) : null}
      {add.state !== undefined && detail !== undefined ? (
        <MeAddSheet
          open
          unit={unit ?? GENERIC_VOCABULARY.capacityUnit.singular}
          nightWords={add.state.nightWords}
          choices={add.state.choices}
          counts={add.state.counts}
          currency={detail.order.total.currency}
          step={add.state.step}
          busy={add.state.busy}
          error={add.state.error}
          readError={add.state.readError}
          onReread={add.reread}
          onCount={add.count}
          onContinue={add.proceed}
          onPay={add.pay}
          onClose={add.close}
        />
      ) : null}
      </>
    );
  if (session !== undefined && props.route.membership === true && tenantId !== undefined)
    return (
      <MeMembershipPage
        email={session.email}
        businessName={businessName}
        logoUrl={logoUrl}
        greeting={portal.members === undefined ? undefined : memberGreeting(portal.members)}
        faces={portal.members === undefined ? undefined : membershipFaces(portal.members, portal.plans, { timeZone: venueZone })}
        error={portal.error}
        ordersHref={meOrdersUrl(tenantId)}
        joinHref={`/memberships?tenant=${encodeURIComponent(tenantId)}`}
        onRetry={portal.retry}
        onRenew={renewal.renew}
        renewing={renewal.renewing}
        cardStep={
          renewal.card === undefined ? undefined : (
            <MembershipCardStep
              renewal
              tierName={renewal.card.tierName}
              note={cardStepNote({ kind: "renewal", termWord: renewal.card.termWord })}
              amount={renewal.card.amount}
              busy={renewal.card.busy}
              error={renewal.card.error}
              mount={(selector) => {
                const card = renewal.card!;
                return (props.stripe ?? (() => loadStripeFactory()))().then((factory) => {
                  const made = createSetupGateway({
                    factory,
                    publishableKey: venue?.publishableKey,
                    ...(venue?.stripeAccount !== undefined ? { stripeAccount: venue.stripeAccount } : {}),
                    clientSecret: card.clientSecret,
                    returnUrl: card.returnUrl,
                    appearance: documentPaymentAppearance(),
                  });
                  if (made.gateway === undefined) return { unavailable: made.unavailable };
                  made.gateway.mount(selector);
                  return { gateway: made.gateway };
                });
              }}
              onPay={renewal.payCard}
              onBack={renewal.closeCard}
            />
          )
        }
        onRedeemGift={gifting.redeem}
        redeeming={gifting.redeeming}
        onSignOut={() => {
          store?.clear();
          setSession(undefined);
          setPhase({ kind: "ask" });
        }}
      />
    );
  if (session !== undefined)
    return (
      <MeOrders
        email={session.email}
        businessName={businessName}
        logoUrl={logoUrl}
        {...(tenantId !== undefined && portal.members !== undefined && portal.members.length > 0
          ? { membership: { href: meMembershipUrl(tenantId), words: portal.members.length === 1 ? "You hold a membership here." : `You hold ${String(portal.members.length)} memberships here.` } }
          : {})}
        cards={orders === undefined ? undefined : meCards(orders, props.today ?? meToday(now()))}
        error={ordersError}
        ticketsBusy={ticketsBusy}
        ticketsError={ticketsError}
        onTickets={tickets}
        onManage={(id) => {
          if (tenantId !== undefined) (props.go ?? ((u: string) => window.location.assign(u)))(meOrderUrl(tenantId, id));
        }}
        onRetry={() => void readOrders()}
        onSignOut={() => {
          store?.clear();
          setSession(undefined);
          setOrders(undefined);
          setPhase({ kind: "ask" });
        }}
      />
    );
  if (phase === "opening" || scope === undefined)
    return (
      <MeSignIn
        phase={{ kind: "ask", busy: true }}
        asked={asked}
        {...(askedFor !== undefined ? { askedFor } : {})}
        email={email}
        businessName={businessName}
        logoUrl={logoUrl}
        onEmail={setEmail}
        /* dead-ok: the link is being opened — this face is the ask's busy state; its one door is in flight and the sent card's doors are not drawn */
        onSend={() => undefined}
        onAnother={() => undefined}
      />
    );
  return (
    <MeSignIn
      phase={phase}
      asked={asked}
      {...(askedFor !== undefined ? { askedFor } : {})}
      email={email}
      businessName={businessName}
      logoUrl={logoUrl}
      onEmail={(e) => {
        setEmail(e);
        if (phase.kind === "ask" && phase.error !== undefined) setPhase({ kind: "ask" });
      }}
      onSend={send}
      onAnother={() => {
        setEmail("");
        setPhase({ kind: "ask" });
      }}
    />
  );
}

/* a 30-day session outlives the tab: it is kept in localStorage, under its own key */
function safeSession(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
